// Everything a current sensor's readings can say, computed from HOURLY BUCKETS.
//
// A bucket is one phase in one clock hour of one day — the shape the server's
// GetCurrentSenseHourly returns, and the shape `bucketize()` builds from raw
// rows when that endpoint is not deployed yet. Both apply the same energy rule
// as phase.js energyKWh(): a reading counts until the next reading of its
// phase, and a gap over 2 minutes is charged as 1 minute (the sensor was
// offline, it did not draw that power the whole time).
//
//   bucket = { h, p, n, wh, cov, mw, ma, vmin, vmax, vavg, sw, sva }
//     wh  energy (Wh)        cov  seconds covered by readings
//     mw  peak W  ma peak A  vmin/vmax/vavg volts (null = not reported)
//     sw/sva  Σ watts / Σ volt-amps — their ratio is the power factor
//   day    = { d: 'YYYY-MM-DD', hours: [bucket], peak: { w, at } | null }
//
// Nothing here fetches, stores or renders. energyData.js loads days,
// EnergyPanel draws the report.

import { billFor, marginalRate } from './energyTariff';

// CEA supply-code band for LT supply: 230 V ± 6 %.
export const V_NOMINAL = 230;
export const V_LOW = 216.2;
export const V_HIGH = 243.8;

const HOUR = 3600;
const pad = (x) => String(x).padStart(2, '0');

export const ymd = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
export const parseYmd = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (dt, k) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + k);
const parseLocal = (s) => new Date(String(s).replace('Z', '').replace('T', ' '));

// ── Raw rows → buckets (the client-side twin of GetCurrentSenseHourly) ─────

/**
 * rows: raw CurrentSense rows ({ DateTime, Phase|phase, power, current, voltage })
 * of ONE OR MORE whole days. -> Map<'YYYY-MM-DD', { d, hours, peak }>
 */
export function bucketize(rows) {
  const byPhase = new Map();
  const byInstant = new Map();
  (rows || []).forEach((r) => {
    const t = parseLocal(r.DateTime);
    if (Number.isNaN(+t)) return;
    const p = Number(r.Phase ?? r.phase) || 1;
    const rec = {
      t, p, w: Number(r.power) || 0, a: Number(r.current) || 0, v: Number(r.voltage) || 0,
    };
    if (!byPhase.has(p)) byPhase.set(p, []);
    byPhase.get(p).push(rec);
    const k = +t;
    byInstant.set(k, (byInstant.get(k) || 0) + rec.w);
  });

  const out = new Map();
  const dayOf = (d) => {
    if (!out.has(d)) out.set(d, { d, hours: [], peak: null, idx: new Map() });
    return out.get(d);
  };

  byPhase.forEach((list) => {
    list.sort((a, b) => a.t - b.t);
    list.forEach((r, i) => {
      const next = list[i + 1];
      const gap = next ? (next.t - r.t) / 1000 : null;
      let dt = 0;
      if (gap != null) dt = gap > 120 ? 60 : gap;
      const day = dayOf(ymd(r.t));
      const key = `${r.t.getHours()}|${r.p}`;
      let b = day.idx.get(key);
      if (!b) {
        b = {
          h: r.t.getHours(), p: r.p, n: 0, wh: 0, cov: 0, mw: 0, ma: 0,
          vmin: null, vmax: null, vsum: 0, vn: 0, sw: 0, sva: 0,
        };
        day.idx.set(key, b);
        day.hours.push(b);
      }
      b.n += 1;
      b.wh += (r.w * dt) / HOUR;
      if (gap == null || gap <= 120) b.cov += dt;
      b.mw = Math.max(b.mw, r.w);
      b.ma = Math.max(b.ma, r.a);
      if (r.v > 0) {
        b.vmin = b.vmin == null ? r.v : Math.min(b.vmin, r.v);
        b.vmax = b.vmax == null ? r.v : Math.max(b.vmax, r.v);
        b.vsum += r.v;
        b.vn += 1;
      }
      b.sw += r.w;
      b.sva += r.v * r.a;
    });
  });

  byInstant.forEach((w, k) => {
    const t = new Date(k);
    const day = dayOf(ymd(t));
    if (!day.peak || w > day.peak.w) {
      day.peak = { w, at: `${ymd(t)} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}` };
    }
  });

  out.forEach((day) => {
    day.hours = day.hours.map(({ vsum, vn, ...b }) => ({
      ...b,
      wh: Math.round(b.wh * 100) / 100,
      vavg: vn ? Math.round((vsum / vn) * 10) / 10 : null,
    })).sort((a, b) => a.h - b.h || a.p - b.p);
    delete day.idx;
  });
  return out;
}

/** Server reply ({ hours: [{ d, ... }], peaks }) → Map<'YYYY-MM-DD', day>. */
export function groupServerHours(reply) {
  const out = new Map();
  const dayOf = (d) => {
    if (!out.has(d)) out.set(d, { d, hours: [], peak: null });
    return out.get(d);
  };
  (reply.hours || []).forEach(({ d, ...b }) => dayOf(d).hours.push(b));
  (reply.peaks || []).forEach((pk) => { dayOf(pk.d).peak = { w: pk.w, at: pk.at }; });
  return out;
}

// ── Billing periods ────────────────────────────────────────────────────────

/**
 * The billing period that contains `date`, for a meter read on `cycleDay`.
 * [start, end) — end is the next reading date. The bill is named after the
 * END month: read 23-05 → 23-06 is MSEDCL's "Jun-2026" bill.
 */
export function periodOf(date, cycleDay = 1) {
  const c = Math.min(28, Math.max(1, Number(cycleDay) || 1));
  const y = date.getFullYear();
  const m = date.getMonth();
  const start = date.getDate() >= c ? new Date(y, m, c) : new Date(y, m - 1, c);
  const end = new Date(start.getFullYear(), start.getMonth() + 1, c);
  return { start, end, label: `${end.getFullYear()}-${pad(end.getMonth() + 1)}` };
}

/** Every period from the one holding `first` to the one holding `last`, newest first. */
export function periodsBetween(first, last, cycleDay) {
  const out = [];
  let p = periodOf(last, cycleDay);
  const stop = periodOf(first, cycleDay).start;
  while (p.start >= stop && out.length < 120) {
    out.push(p);
    p = periodOf(addDays(p.start, -1), cycleDay);
  }
  return out;
}

export const monthName = (label) => {
  const [y, m] = label.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

// ── The report ─────────────────────────────────────────────────────────────

const isOff = (d, offDays) => (offDays || []).includes(parseYmd(d).getDay());

/** Seconds of day `d` that SHOULD have readings, hour by hour (0 in the future). */
function expectedByHour(d, now) {
  const day = parseYmd(d);
  const out = new Array(24).fill(0);
  for (let h = 0; h < 24; h += 1) {
    const s = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h);
    const e = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h + 1);
    if (now >= e) out[h] = HOUR;
    else if (now > s) out[h] = Math.round((now - s) / 1000);
  }
  return out;
}

function median(a) {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * days: day objects for [from, to) — absent days are days with no readings.
 * settings: { tariff, offDays, workStart, workEnd, sanctionedKw }
 * opts: { from: Date, to: Date (exclusive), now: Date, firstData: 'YYYY-MM-DD'|null,
 *         phases: number[] }
 */
export function analyse(days, settings, opts) {
  const now = opts.now || new Date();
  const { offDays = [], workStart = 9, workEnd = 21 } = settings;
  const phases = opts.phases && opts.phases.length ? opts.phases : [1, 2, 3];
  const byDay = new Map((days || []).map((d) => [d.d, d]));
  const firstData = opts.firstData || null;

  const list = [];
  for (let dt = new Date(opts.from); dt < opts.to; dt = addDays(dt, 1)) list.push(ymd(dt));

  // Average watts per (type, hour, phase) — the basis for estimates and the profile.
  const acc = { work: [], off: [] };
  ['work', 'off'].forEach((k) => { for (let h = 0; h < 24; h += 1) acc[k][h] = {}; });

  const daily = [];
  const v = {};
  const pf = {};
  const peakA = {};
  phases.forEach((p) => {
    v[p] = { min: null, max: null, sum: 0, n: 0, hoursHigh: 0, hoursLow: 0 };
    pf[p] = { sw: 0, sva: 0 };
    peakA[p] = { a: 0, d: null };
  });
  const baseSamples = [];

  list.forEach((d) => {
    const day = byDay.get(d);
    const type = isOff(d, offDays) ? 'off' : 'work';
    const expected = firstData && d < firstData ? new Array(24).fill(0) : expectedByHour(d, now);
    const kwh = {};
    phases.forEach((p) => { kwh[p] = 0; });
    const covH = new Array(24).fill(0);
    const totWByHour = new Array(24).fill(null);

    (day ? day.hours : []).forEach((b) => {
      if (!phases.includes(b.p)) return;
      kwh[b.p] += b.wh / 1000;
      covH[b.h] = Math.max(covH[b.h], Math.min(HOUR, b.cov));
      if (b.cov > 0) {
        const cell = acc[type][b.h];
        cell[b.p] = cell[b.p] || { wh: 0, cov: 0 };
        cell[b.p].wh += b.wh;
        cell[b.p].cov += Math.min(HOUR, b.cov);
        totWByHour[b.h] = (totWByHour[b.h] || 0) + (b.wh / Math.min(HOUR, b.cov)) * HOUR;
      }
      if (b.ma > peakA[b.p].a) peakA[b.p] = { a: b.ma, d };
      const vv = v[b.p];
      if (b.vmin != null && b.vmin > 0) vv.min = vv.min == null ? b.vmin : Math.min(vv.min, b.vmin);
      if (b.vmax != null && b.vmax > 0) vv.max = vv.max == null ? b.vmax : Math.max(vv.max, b.vmax);
      if (b.vavg != null && b.vavg > 0) {
        vv.sum += b.vavg * b.n;
        vv.n += b.n;
        if (b.vavg > V_HIGH) vv.hoursHigh += 1;
        if (b.vavg < V_LOW) vv.hoursLow += 1;
      }
      if (b.n && b.sw / b.n > 50 && b.sva > 0) {
        pf[b.p].sw += b.sw;
        pf[b.p].sva += b.sva;
      }
    });

    for (let h = 1; h <= 4; h += 1) {
      if (totWByHour[h] != null && covH[h] >= HOUR / 2) baseSamples.push(totWByHour[h]);
    }

    const missH = expected.map((e, h) => Math.max(0, e - covH[h]));
    const total = phases.reduce((a, p) => a + kwh[p], 0);
    let offHours = 0;
    (day ? day.hours : []).forEach((b) => {
      if (!phases.includes(b.p)) return;
      if (type === 'off' || b.h < workStart || b.h >= workEnd) offHours += b.wh / 1000;
    });

    daily.push({
      d,
      type,
      kwh,
      total,
      offHours,
      peak: day && day.peak ? day.peak : null,
      expectedS: expected.reduce((a, x) => a + x, 0),
      missingS: missH.reduce((a, x) => a + x, 0),
      missH,
    });
  });

  // Profile: average watts per hour per phase, for work days and off days.
  const profile = {};
  ['work', 'off'].forEach((k) => {
    profile[k] = acc[k].map((cell, h) => {
      const row = { h };
      let any = false;
      phases.forEach((p) => {
        const c = cell[p];
        row[`p${p}`] = c && c.cov ? Math.round((c.wh / c.cov) * HOUR) : null;
        if (row[`p${p}`] != null) any = true;
      });
      row.has = any;
      return row;
    });
  });
  const avgWAt = (type, h) => {
    const row = profile[type][h].has ? profile[type][h] : profile[type === 'off' ? 'work' : 'off'][h];
    if (!row.has) return 0;
    return phases.reduce((a, p) => a + (row[`p${p}`] || 0), 0);
  };

  // Units the gaps swallowed, priced from the profile of the same day type and hour.
  let estWh = 0;
  daily.forEach((x) => {
    x.estKwh = x.missH.reduce((a, s, h) => a + (s * avgWAt(x.type, h)) / HOUR, 0) / 1000;
    estWh += x.estKwh * 1000;
  });

  const recorded = daily.reduce((a, x) => a + x.total, 0);
  const byPhase = {};
  phases.forEach((p) => { byPhase[p] = daily.reduce((a, x) => a + x.kwh[p], 0); });

  // Office hours as the load shows them: the first and last work-day hour whose
  // load stands clearly above the night base.
  const baseW = Math.round(median(baseSamples));
  const workTotals = profile.work.map((r) => (r.has ? phases.reduce((a, p) => a + (r[`p${p}`] || 0), 0) : null));
  const busy = workTotals.map((w) => w != null && w > baseW + Math.max(150, baseW * 0.5));
  const opens = busy.indexOf(true);
  const closes = busy.lastIndexOf(true);

  const offDayKwh = daily.filter((x) => x.type === 'off').reduce((a, x) => a + x.total, 0);
  const offHoursKwh = daily.reduce((a, x) => a + x.offHours, 0);
  const coveredDays = daily.reduce((a, x) => a + (x.expectedS - x.missingS), 0) / 86400;
  const rate = marginalRate(settings.tariff);
  const perYear = (kwh) => (coveredDays > 0 ? (kwh / coveredDays) * 365 : 0);

  const peaks = daily.filter((x) => x.peak).map((x) => ({ d: x.d, w: x.peak.w, at: x.peak.at }));
  const top = peaks.reduce((a, x) => (!a || x.w > a.w ? x : a), null);

  // Gaps: runs of hours with at least 5 minutes missing, merged.
  const gaps = [];
  let open = null;
  daily.forEach((x) => {
    x.missH.forEach((s, h) => {
      const at = `${x.d} ${pad(h)}:00`;
      if (s >= 300) {
        if (!open) open = { from: at, to: null, missingS: 0, hours: 0 };
        open.missingS += s;
        open.hours += 1;
        open.to = `${x.d} ${pad(h + 1)}:00`;
      } else if (open) {
        gaps.push(open);
        open = null;
      }
    });
  });
  if (open) gaps.push(open);

  const voltage = {};
  phases.forEach((p) => {
    const vv = v[p];
    voltage[p] = {
      min: vv.min,
      max: vv.max,
      avg: vv.n ? Math.round((vv.sum / vv.n) * 10) / 10 : null,
      hoursHigh: vv.hoursHigh,
      hoursLow: vv.hoursLow,
    };
  });
  const powerFactor = {};
  phases.forEach((p) => {
    powerFactor[p] = pf[p].sva > 0 ? Math.min(1, pf[p].sw / pf[p].sva) : null;
  });

  // Balance on WORKING-hour energy — at night one phase carries the base load
  // alone, which says nothing about how the loads are spread.
  const workKwh = {};
  phases.forEach((p) => { workKwh[p] = 0; });
  list.forEach((d) => {
    const day = byDay.get(d);
    if (!day || isOff(d, offDays)) return;
    day.hours.forEach((b) => {
      if (phases.includes(b.p) && b.h >= workStart && b.h < workEnd) workKwh[b.p] += b.wh / 1000;
    });
  });
  const wk = phases.map((p) => workKwh[p]);
  const wkAvg = wk.reduce((a, x) => a + x, 0) / (wk.length || 1);
  const imbalancePct = phases.length > 1 && wkAvg > 0
    ? Math.round((Math.max(...wk.map((x) => Math.abs(x - wkAvg))) / wkAvg) * 100) : 0;

  return {
    phases,
    daily,
    profile,
    recordedKwh: recorded,
    estimatedKwh: estWh / 1000,
    byPhase,
    coveragePct: daily.reduce((a, x) => a + x.expectedS, 0)
      ? Math.round((1 - daily.reduce((a, x) => a + x.missingS, 0) / daily.reduce((a, x) => a + x.expectedS, 0)) * 1000) / 10
      : 0,
    baseW,
    opensAt: opens >= 0 ? opens : null,
    closesAt: closes >= 0 ? closes + 1 : null,
    offDayKwh,
    offHoursKwh,
    waste: {
      baseKwhYear: (baseW * 8760) / 1000,
      baseRupeesYear: ((baseW * 8760) / 1000) * rate,
      offHoursRupeesYear: perYear(offHoursKwh) * rate,
      rate,
    },
    peak: top,
    peaks,
    peakA,
    voltage,
    powerFactor,
    workKwh,
    imbalancePct,
    gaps,
  };
}

/**
 * Units for the whole period: recorded so far + gaps estimated + the rest of
 * the period, at each day type's average from the COMPLETE reference days.
 * reference: analyse() daily rows of complete days (any window, e.g. the last 30).
 */
export function projectPeriod(report, period, reference, offDays, now = new Date()) {
  const complete = (reference || []).filter((x) => x.expectedS >= 86400 && x.missingS < 3 * HOUR);
  const avgOf = (type) => {
    const xs = complete.filter((x) => x.type === type);
    if (!xs.length) return null;
    return xs.reduce((a, x) => a + x.total + x.estKwh, 0) / xs.length;
  };
  const avg = { work: avgOf('work'), off: avgOf('off') };
  if (avg.work == null) avg.work = avg.off ?? 0;
  if (avg.off == null) avg.off = avg.work;

  let rest = 0;
  const today = ymd(now);
  for (let dt = new Date(period.start); dt < period.end; dt = addDays(dt, 1)) {
    const d = ymd(dt);
    const type = isOff(d, offDays) ? 'off' : 'work';
    if (d > today) rest += avg[type];
    else if (d === today) {
      const sofar = report.daily.find((x) => x.d === d);
      rest += Math.max(0, avg[type] - (sofar ? sofar.total + sofar.estKwh : 0));
    }
  }
  const sofar = report.recordedKwh + report.estimatedKwh;
  return { sofar, rest, total: sofar + rest, avgWorkDay: avg.work, avgOffDay: avg.off };
}

/** Months the period spans, for prorating slabs (a 31-day period is 1.0). */
export function periodMonths(period) {
  const days = Math.round((period.end - period.start) / 86400000);
  return days / 30.4375 > 0.9 && days / 30.4375 < 1.1 ? 1 : days / 30.4375;
}

/** Bill check: the sensor's units for a bill's period against the bill's own. */
export function compareBill(bill, sensorKwh, tariff, sanctionedKw) {
  const units = Number(bill.units) || 0;
  const amount = Number(bill.amount) || billFor(units, tariff, { sanctionedKw }).total;
  const diffPct = units > 0 ? ((sensorKwh - units) / units) * 100 : null;
  return {
    units, amount, sensorKwh, diffPct, ratio: sensorKwh > 0 ? units / sensorKwh : null,
  };
}
